import { useState, useRef, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { getIncidentTypeById } from '@/mocks/preInspection';

interface IncidentItem {
  id: string;
  studentId: string;
  date: string;
  incidentTypeId: string;
  severity: string;
  description: string;
}

interface IncidentTooltipProps {
  show: boolean;
  incidents: IncidentItem[];
  triggerRect: DOMRect | null;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}

const severityColors: Record<string, string> = {
  'Minor': 'bg-yellow-400', 'Moderate': 'bg-orange-400', 'Major': 'bg-red-400', 'Severe': 'bg-red-500',
  'Serious': 'bg-red-500', 'Critical': 'bg-red-600', 'Attempt': 'bg-yellow-400', 'Brief': 'bg-orange-400', 'Extended': 'bg-red-500',
};

export default function IncidentTooltip({ show, incidents, triggerRect, onMouseEnter, onMouseLeave }: IncidentTooltipProps) {
  const [visible, setVisible] = useState(false);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearHideTimer = useCallback(() => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (show) {
      clearHideTimer();
      setVisible(true);
    } else {
      clearHideTimer();
      hideTimerRef.current = setTimeout(() => {
        setVisible(false);
      }, 150);
    }
    return () => {
      clearHideTimer();
    };
  }, [show, clearHideTimer]);

  if (!visible || !triggerRect || incidents.length === 0) return null;

  const latestIncident = incidents[incidents.length - 1];
  const severityColor = severityColors[latestIncident.severity] || 'bg-red-500';

  const left = triggerRect.left + triggerRect.width / 2;
  const top = triggerRect.top;

  // Check viewport edges and flip if needed
  const tooltipHeightEstimate = 180;
  const tooltipWidth = 288; // w-72
  const gap = 8;
  let finalTop = top;
  let finalTransform = 'translate(-50%, calc(-100% - 8px))';
  let arrowPosition: 'bottom' | 'top' = 'bottom';

  if (top - tooltipHeightEstimate - gap < 0) {
    // Not enough room above — place below
    finalTop = top + triggerRect.height;
    finalTransform = 'translate(-50%, 8px)';
    arrowPosition = 'top';
  }

  // Clamp horizontal position to viewport
  const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 1024;
  let finalLeft = left;
  const halfWidth = tooltipWidth / 2;
  if (left - halfWidth < 8) {
    finalLeft = halfWidth + 8;
  } else if (left + halfWidth > viewportWidth - 8) {
    finalLeft = viewportWidth - halfWidth - 8;
  }

  return createPortal(
    <div
      className="fixed z-[9999] animate-[modalEnter_150ms_cubic-bezier(0.4,0,0.2,1)]"
      style={{ left: finalLeft, top: finalTop, transform: finalTransform }}
      onMouseEnter={() => {
        clearHideTimer();
        onMouseEnter();
      }}
      onMouseLeave={onMouseLeave}
    >
      <div className="bg-background-50 rounded-xl border border-background-200 shadow-floating p-4 w-72 text-left relative">
        <div className="flex items-center gap-2 mb-2">
          <div className={`w-2.5 h-2.5 rounded-full ${severityColor} flex-shrink-0`} />
          <p className="text-xs font-label font-semibold text-foreground-900">
            {incidents.length} {incidents.length === 1 ? 'incident' : 'incidents'}
          </p>
        </div>
        {incidents.map((inc, i) => {
          const it = getIncidentTypeById(inc.incidentTypeId);
          const sc = severityColors[inc.severity] || 'bg-red-500';
          return (
            <div key={inc.id} className={`${i > 0 ? 'mt-2.5 pt-2.5 border-t border-background-100' : ''}`}>
              <div className="flex items-center gap-1.5 mb-1">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className={`${it?.icon || 'ri-alert-line'} text-red-500 text-xs`} />
                </div>
                <span className="text-xs font-label font-semibold text-foreground-800">{it?.label || inc.incidentTypeId}</span>
              </div>
              <div className="flex items-center gap-2 ml-1 mb-1">
                <span className={`inline-block w-1.5 h-1.5 rounded-full ${sc}`} />
                <span className="text-[11px] font-label text-foreground-600">{inc.severity}</span>
                <span className="text-[11px] text-foreground-400">&middot;</span>
                <span className="text-[11px] font-label text-foreground-400">{new Date(inc.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}</span>
              </div>
              <p className="text-[11px] text-foreground-500 leading-relaxed line-clamp-2 ml-1">{inc.description}</p>
            </div>
          );
        })}
        {/* Arrow */}
        {arrowPosition === 'bottom' ? (
          <div className="w-3 h-3 bg-background-50 border-l border-t border-background-200 rotate-45 absolute -bottom-1.5 left-1/2 -translate-x-1/2" />
        ) : (
          <div className="w-3 h-3 bg-background-50 border-r border-b border-background-200 rotate-45 absolute -top-1.5 left-1/2 -translate-x-1/2" />
        )}
      </div>
    </div>,
    document.body,
  );
}